// src/CqcContributorTask.js
// =============================================================================
// The contributor-task portal — the page a "?cqcTask=TOKEN" link (emailed
// from CqcEvidenceDialog.js's "Ask a contributor" panel) opens. No login,
// no nav rail, one element: a contributor sets where the evidence for ONE
// quality statement's ONE evidence category lives, leaves a comment on
// progress, and can read/reply to messages from a supervisor — all scoped
// to exactly the (site, groupId, itemId, elementId) the token was minted
// for (see cqc.js's verifyTaskToken: nothing here is ever taken from the
// URL/body, only from the verified token).
//
// Rendered by App.js BEFORE <AuthedApp/>, the same way SinglePharmacy's
// "?spview=TOKEN" read-only link works — see this component's header
// comment in App.js for the early-return wiring.
//
// This page purposefully does NOT ship its own copy of the taxonomy's
// purpose/proof-needed text from the backend — it already has the whole
// taxonomy bundled (cqcTaxonomy.js is a frontend module either way), so it
// looks the guidance up itself from the (groupId, itemId, elementId) the
// context call returns, via the same elementGuidance() the Worklist and
// evidence dialog use. That keeps cqc.js from needing its own taxonomy copy
// to keep in sync.
// =============================================================================

import React, { useEffect, useState } from "react";
import { cqcTaskApi } from "./cqcApi";
import { elementGuidance } from "./cqcTaxonomy";
import { MAX_UPLOAD_BYTES, fmtBytes, readFileAsBase64 } from "./CqcEvidenceDialog";

const S = {
  page: { minHeight: "100vh", background: "#f3f4f6", fontFamily: "system-ui, sans-serif",
          padding: "32px 16px", display: "flex", justifyContent: "center" },
  card: { background: "#fff", borderRadius: 12, width: 640, maxWidth: "100%", padding: 28,
          boxShadow: "0 4px 24px rgba(0,0,0,0.08)" },
  brand: { fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 },
  h1: { fontSize: 20, fontWeight: 700, margin: "0 0 4px", color: "#111827" },
  sub: { fontSize: 13, color: "#6b7280", marginBottom: 18 },
  background: { fontSize: 13, lineHeight: 1.5, color: "#374151", background: "#f8fafc",
                border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px", marginBottom: 18 },
  section: { marginTop: 22 },
  sectionTitle: { fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase",
                  letterSpacing: 0.3, marginBottom: 8 },
  purpose: { fontSize: 13.5, color: "#374151", lineHeight: 1.5 },
  bullets: { margin: "6px 0 0", paddingLeft: 18, color: "#475569", fontSize: 13 },
  label: { fontSize: 12, fontWeight: 600, color: "#374151", margin: "10px 0 4px", display: "block" },
  input: { width: "100%", boxSizing: "border-box", padding: "9px 11px", fontSize: 13.5,
           border: "1px solid #d1d5db", borderRadius: 6, fontFamily: "inherit" },
  textarea: { width: "100%", boxSizing: "border-box", padding: "9px 11px", fontSize: 13.5, minHeight: 70,
              border: "1px solid #d1d5db", borderRadius: 6, fontFamily: "inherit", resize: "vertical" },
  select: { width: "100%", padding: "9px 11px", fontSize: 13.5, border: "1px solid #d1d5db", borderRadius: 6 },
  toggleModeRow: { display: "flex", gap: 6, margin: "4px 0 2px" },
  modeBtn: (active) => ({ flex: 1, padding: "8px 10px", fontSize: 12.5, fontWeight: 600, borderRadius: 6, cursor: "pointer",
                           border: active ? "1px solid #1e293b" : "1px solid #d1d5db",
                           background: active ? "#1e293b" : "#fff", color: active ? "#fff" : "#374151" }),
  btn: { padding: "9px 16px", fontSize: 13.5, fontWeight: 600, border: "none", borderRadius: 6,
         background: "#2563eb", color: "#fff", cursor: "pointer" },
  btnDark: { padding: "9px 16px", fontSize: 13.5, fontWeight: 600, border: "none", borderRadius: 6,
             background: "#1e293b", color: "#fff", cursor: "pointer" },
  ok: { fontSize: 12.5, color: "#166534", background: "#dcfce7", border: "1px solid #86efac",
        borderRadius: 6, padding: "7px 10px", marginTop: 10 },
  err: { fontSize: 12.5, color: "#991b1b", background: "#fee2e2", border: "1px solid #fca5a5",
         borderRadius: 6, padding: "7px 10px", marginTop: 10 },
  existing: { fontSize: 12.5, color: "#374151", background: "#f9fafb", border: "1px solid #eef2f7",
              borderRadius: 6, padding: "9px 11px", marginBottom: 12 },
  link: { color: "#2563eb", fontWeight: 600, textDecoration: "none" },
  hint: { fontSize: 11.5, color: "#9ca3af", marginTop: 4 },
  msgList: { display: "flex", flexDirection: "column", gap: 8, margin: "8px 0", maxHeight: 220, overflow: "auto" },
  msg: (role) => ({ padding: "8px 11px", borderRadius: 8, fontSize: 12.5,
                     background: role === "supervisor" ? "#eff6ff" : "#fff",
                     border: `1px solid ${role === "supervisor" ? "#bfdbfe" : "#e5e7eb"}` }),
  msgMeta: { fontSize: 10.5, color: "#94a3b8", marginBottom: 3 },
  msgForm: { display: "flex", gap: 8, marginTop: 6 },
  centerMsg: { textAlign: "center", padding: "60px 20px", color: "#6b7280", fontSize: 14 },
};

const REASON_TEXT = {
  invalid: "This link isn't valid. It may have been copied incorrectly — ask whoever sent it to send it again.",
  expired: "This link has expired.",
  consumed: "This link is no longer active — a newer link may have been sent since. Ask whoever sent it for the latest one.",
};

export default function CqcContributorTask({ token }) {
  // `ctx`/`error` hold what's currently on screen; `initialLoading` gates
  // ONLY the very first fetch. A later refresh (after saving a source, a
  // comment, or a message — see onChanged below) updates ctx in place
  // without ever unmounting TaskCard, so a child form's own transient
  // state (e.g. SourceForm's "Saved." confirmation) survives the refresh
  // instead of flashing away the instant it appears.
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    cqcTaskApi.context(token).then((res) => {
      if (cancelled) return;
      if (!res.ok) { setError(res.reason || "invalid"); setCtx(null); }
      else { setCtx(res); setError(null); }
      setInitialLoading(false);
    }).catch(() => {
      if (!cancelled) { setError("invalid"); setInitialLoading(false); }
    });
    return () => { cancelled = true; };
  }, [token, reload]);

  if (initialLoading) {
    return <div style={S.page}><div style={S.card}><div style={S.centerMsg}>Loading…</div></div></div>;
  }
  if (error) {
    return (
      <div style={S.page}>
        <div style={S.card}>
          <div style={S.centerMsg}>{REASON_TEXT[error] || REASON_TEXT.invalid}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <TaskCard token={token} ctx={ctx} onChanged={() => setReload((n) => n + 1)} />
    </div>
  );
}

function TaskCard({ token, ctx, onChanged }) {
  const guidance = elementGuidance(ctx.groupId, ctx.itemId, ctx.elementId);
  const existingDocRef = ctx.attestation && ctx.attestation.documentRef && ctx.attestation.documentRef.kind === "document"
    ? ctx.attestation.documentRef : null;
  const reviewPassed = existingDocRef && existingDocRef.reviewDate && new Date(existingDocRef.reviewDate).getTime() < Date.now();

  return (
    <div style={S.card}>
      <div style={S.brand}>CQC Compliance</div>
      <h1 style={S.h1}>{guidance.itemLabel}</h1>
      <div style={S.sub}>{ctx.site.name} — {guidance.categoryLabel}{ctx.contributorName ? ` · assigned to ${ctx.contributorName}` : ""}</div>

      {ctx.background && <div style={S.background}>{ctx.background}</div>}

      <div style={S.section}>
        <div style={S.sectionTitle}>What this is for</div>
        <div style={S.purpose}>{guidance.purpose}</div>
      </div>

      <div style={S.section}>
        <div style={S.sectionTitle}>What proof is needed</div>
        <div style={S.purpose}>{guidance.categoryHelp}</div>
        {guidance.proofExamples && guidance.proofExamples.length > 0 && (
          <ul style={S.bullets}>{guidance.proofExamples.map((p, i) => <li key={i}>{p}</li>)}</ul>
        )}
      </div>

      {ctx.attestation && (
        <div style={{ ...S.section }}>
          <div style={S.sectionTitle}>Currently on file</div>
          <div style={S.existing}>
            <strong>{ctx.attestation.source || "—"}</strong>
            {existingDocRef && existingDocRef.storageMode === "link" && existingDocRef.url && (
              <> — <a style={S.link} href={existingDocRef.url} target="_blank" rel="noreferrer">open link</a></>
            )}
            {existingDocRef && existingDocRef.storageMode === "upload" && existingDocRef.documentId && (
              <>
                {" — "}
                <a style={S.link} href={cqcTaskApi.documentUrl(token, existingDocRef.documentId)} target="_blank" rel="noreferrer">view</a>
                {" · "}
                <a style={S.link} href={cqcTaskApi.documentUrl(token, existingDocRef.documentId, true)}>download</a>
              </>
            )}
            {existingDocRef && existingDocRef.reviewDate && (
              <div style={reviewPassed ? { color: "#991b1b", fontWeight: 600, marginTop: 4 } : { color: "#6b7280", marginTop: 4 }}>
                Review date {new Date(existingDocRef.reviewDate).toLocaleDateString()}{reviewPassed ? " (passed)" : ""}
              </div>
            )}
          </div>
        </div>
      )}

      <SourceForm token={token} existingDocRef={existingDocRef} onSaved={onChanged} />
      <CommentForm token={token} assignment={ctx.assignment} onSaved={onChanged} />
      <MessagesBlock token={token} messages={ctx.messages} />
    </div>
  );
}

function SourceForm({ token, existingDocRef, onSaved }) {
  const [storageMode, setStorageMode] = useState((existingDocRef && existingDocRef.storageMode) || "link");
  const [docLink, setDocLink] = useState((existingDocRef && existingDocRef.storageMode === "link" && existingDocRef.url) || "");
  const [reviewDate, setReviewDate] = useState(existingDocRef && existingDocRef.reviewDate ? new Date(existingDocRef.reviewDate).toISOString().slice(0, 10) : "");
  const [pendingFile, setPendingFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  async function handleFileSelect(e) {
    setError(null);
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      setError(`"${file.name}" is ${fmtBytes(file.size)} — the limit is ${fmtBytes(MAX_UPLOAD_BYTES)}. Try linking to it instead.`);
      e.target.value = "";
      return;
    }
    try {
      const fileBase64 = await readFileAsBase64(file);
      setPendingFile({ filename: file.name, contentType: file.type || "application/octet-stream", fileBase64, bytes: file.size });
    } catch (err) {
      setError(err.message);
    }
  }

  async function save() {
    setSaving(true); setError(null); setSaved(false);
    try {
      let documentRef;
      if (storageMode === "upload") {
        if (pendingFile) {
          documentRef = { kind: "document", storageMode: "upload", filename: pendingFile.filename,
                           contentType: pendingFile.contentType, fileBase64: pendingFile.fileBase64,
                           reviewDate: reviewDate || null };
        } else if (existingDocRef && existingDocRef.storageMode === "upload") {
          documentRef = { kind: "document", storageMode: "upload", documentId: existingDocRef.documentId, reviewDate: reviewDate || null };
        } else {
          throw new Error("Choose a file to upload");
        }
      } else {
        if (!docLink.trim()) throw new Error("Enter a link to the document");
        documentRef = { kind: "document", storageMode: "link", url: docLink.trim(), reviewDate: reviewDate || null };
      }
      await cqcTaskApi.setSource(token, documentRef, storageMode === "upload" ? "Uploaded document" : "Linked document");
      setSaved(true);
      onSaved && onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={S.section}>
      <div style={S.sectionTitle}>Set the evidence source</div>
      <div style={S.toggleModeRow}>
        <button type="button" style={S.modeBtn(storageMode === "link")} onClick={() => setStorageMode("link")}>Link to it</button>
        <button type="button" style={S.modeBtn(storageMode === "upload")} onClick={() => setStorageMode("upload")}>Upload it here</button>
      </div>
      {storageMode === "link" ? (
        <>
          <label style={S.label}>Link</label>
          <input style={S.input} value={docLink} onChange={(e) => setDocLink(e.target.value)}
                 placeholder="Link to the document (SharePoint, Google Drive, network path...)" />
        </>
      ) : (
        <>
          <label style={S.label}>File</label>
          <input type="file" onChange={handleFileSelect} />
          {pendingFile && <div style={S.hint}>Selected: {pendingFile.filename} ({fmtBytes(pendingFile.bytes)})</div>}
          {!pendingFile && existingDocRef && existingDocRef.storageMode === "upload" && (
            <div style={S.hint}>Keeping the current file: {existingDocRef.filename}. Choose a new one to replace it.</div>
          )}
          <div style={S.hint}>Up to {fmtBytes(MAX_UPLOAD_BYTES)}.</div>
        </>
      )}
      <label style={S.label}>Review date (if this needs periodic renewal — e.g. an insurance certificate)</label>
      <input type="date" style={S.input} value={reviewDate} onChange={(e) => setReviewDate(e.target.value)} />
      {error && <div style={S.err}>{error}</div>}
      {saved && <div style={S.ok}>Saved.</div>}
      <div style={{ marginTop: 10 }}>
        <button style={S.btn} onClick={save} disabled={saving}>{saving ? "Saving…" : "Save evidence source"}</button>
      </div>
    </div>
  );
}

function CommentForm({ token, assignment, onSaved }) {
  const [comment, setComment] = useState((assignment && assignment.contributorComment) || "");
  const [date, setDate] = useState(assignment && assignment.estimatedCompletionDate ? new Date(assignment.estimatedCompletionDate).toISOString().slice(0, 10) : "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  async function save() {
    setSaving(true); setSaved(false);
    try {
      await cqcTaskApi.saveComment(token, { contributorComment: comment, estimatedCompletionDate: date || null });
      setSaved(true);
      onSaved && onSaved();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={S.section}>
      <div style={S.sectionTitle}>How this will be achieved</div>
      <textarea style={S.textarea} value={comment} onChange={(e) => setComment(e.target.value)}
                placeholder="e.g. Policy drafted, awaiting sign-off" />
      <label style={S.label}>Estimated completion date</label>
      <input type="date" style={S.input} value={date} onChange={(e) => setDate(e.target.value)} />
      {saved && <div style={S.ok}>Saved.</div>}
      <div style={{ marginTop: 10 }}>
        <button style={S.btn} onClick={save} disabled={saving}>{saving ? "Saving…" : "Save comment"}</button>
      </div>
    </div>
  );
}

function MessagesBlock({ token, messages: initialMessages }) {
  const [messages, setMessages] = useState(initialMessages || []);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => { setMessages(initialMessages || []); }, [initialMessages]);

  async function send() {
    if (!body.trim()) return;
    setSending(true);
    try {
      await cqcTaskApi.addMessage(token, body.trim());
      setBody("");
      const res = await cqcTaskApi.listMessages(token);
      setMessages(res.messages || []);
    } finally {
      setSending(false);
    }
  }

  return (
    <div style={S.section}>
      <div style={S.sectionTitle}>Messages ({messages.length})</div>
      {messages.length > 0 && (
        <div style={S.msgList}>
          {messages.map((m) => (
            <div key={m._id} style={S.msg(m.authorRole)}>
              <div style={S.msgMeta}>
                {m.authorRole === "supervisor" ? "Supervisor" : "You"} · {new Date(m.createdAt).toLocaleString()}
              </div>
              {m.body}
            </div>
          ))}
        </div>
      )}
      <div style={S.msgForm}>
        <input style={{ ...S.input, flex: 1 }} placeholder="Add a message…" value={body}
               onChange={(e) => setBody(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter") send(); }} />
        <button style={S.btnDark} onClick={send} disabled={sending || !body.trim()}>Send</button>
      </div>
    </div>
  );
}
